import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Router} from '@angular/router';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {AngularFireAnalytics} from '@angular/fire/compat/analytics';
import {NGXLogger} from 'ngx-logger';
import {Observable, throwError} from 'rxjs';
import {Events} from '../models/analytics';
import {SESSION_EXPIRED_REASON, SessionExpiredError} from '../models/session-expired-error';

const LOGIN_ROUTE = '/s/login';

// The chat holds the file and the answers in memory until the submission is
// posted, so an expiry here loses them.
const SUBMISSION_ROUTES = ['/s', '/s/data'];

const pathOf = (url: string): string => url.split(/[?#;]/)[0];

export const isSubmissionRoute = (url: string): boolean => SUBMISSION_ROUTES.includes(pathOf(url));

export const sessionExpiryCode = (error: HttpErrorResponse): string | undefined => {
  const body = error.error;
  const code = body?.error ?? body?.detail?.error;
  if (typeof code === 'string') {
    return code;
  }
  return error.headers?.get('WWW-Authenticate')?.match(/error_description="([^"]*)"/)?.[1];
};

/**
 * Ends a session the backend no longer accepts and takes the user to the
 * login, which explains why.
 *
 * Injected by the HTTP interceptor, so it must not depend on anything that
 * injects HttpClient.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionExpiryService {
  private expiring: Promise<void> | null = null;

  constructor(
    private angularAuth: AngularFireAuth,
    private router: Router,
    private analytics: AngularFireAnalytics,
    private logger: NGXLogger
  ) {}

  handleUnauthorized(error: HttpErrorResponse): Observable<never> {
    const code = sessionExpiryCode(error);
    this.expire(code).catch((e) => this.logger.error(e));
    return throwError(new SessionExpiredError(error, code));
  }

  /** Concurrent calls share one run: one event, one sign-out, one navigation. */
  expire(code?: string): Promise<void> {
    if (!this.expiring) {
      this.expiring = this.endSession(code).finally(() => {
        this.expiring = null;
      });
    }
    return this.expiring;
  }

  private async endSession(code?: string): Promise<void> {
    // A navigation still resolving (a reloaded tab, a resolver's call) has not
    // moved router.url yet: after the login, go where the user was going.
    const navigation = this.router.getCurrentNavigation();
    const redirect = navigation ? this.router.serializeUrl(navigation.finalUrl || navigation.extractedUrl) : this.router.url;
    const inSubmission = isSubmissionRoute(this.router.url);

    // A 401 that was already in flight when the session ended (a retry, a
    // parallel call) finds no user and has nothing left to do.
    if (!(await this.angularAuth.currentUser)) {
      return;
    }

    this.analytics.logEvent(Events.SESSION_EXPIRED, {
      trigger: 'api_401',
      // A declared 'true'/'false', as enviei.me reports it: both apps feed one GA4 property.
      in_submission: String(inSubmission),
      ...(code ? {code} : {})
    });

    try {
      await this.angularAuth.signOut();
    } catch (e) {
      this.logger.error(e);
    }

    if (pathOf(this.router.url) !== LOGIN_ROUTE) {
      await this.router.navigate([LOGIN_ROUTE], {state: {reason: SESSION_EXPIRED_REASON, redirect}});
    }
  }
}
